import { useState } from 'react';
import { Box, IconButton, Collapse, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { formatNumberByUnits } from 'utils/finopsDataAdapter';
import HighlightedText from 'components/GlobalSearch/HighlightText';

interface ExpandableRowData {
	name: string;
	totalSpent: number;
	percentage: number;
	children?: ExpandableRowData[];
}

interface ExpandableRowProps {
	row: ExpandableRowData;
	level?: number;
	index?: number;
}

const ExpandableTableRow = ({ row, level = 0, index = 0 }: ExpandableRowProps) => {
	const [open, setOpen] = useState(false);
	const hasChildren = row.children && row.children.length > 0;

	const getRowBackground = (theme: any) => {
		if (level === 0) {
			return index % 2 === 0 ? theme.palette.action.hover : theme.palette.common.white;
		}
		return level % 2 === 0 ? theme.palette.action.hover : theme.palette.common.white;
	};

	const getTextColor = (theme: any) => {
		return theme.palette.text.primary;
	};

	const getHoverBackground = (theme: any) => {
		if (level === 0) {
			return index % 2 === 0 ? theme.palette.action.selected : theme.palette.action.hover;
		}
		return theme.palette.action.selected;
	};

	return (
		<>
			<TableRow
				sx={{
					'& > *': { borderBottom: 'unset' },
					backgroundColor: (theme) => getRowBackground(theme),
					'&:hover': {
						backgroundColor: (theme) => `${getHoverBackground(theme)} !important`
					}
				}}
			>
				<TableCell
					sx={{
						'&.MuiTableCell-root': {
							padding: '8px 12px !important',
							color: (theme) => getTextColor(theme),
							backgroundColor: (theme) => `${getRowBackground(theme)} !important`,
							borderRight: (theme) => `1px solid ${theme.palette.white.main} !important`,
							width: 'auto'
						}
					}}
				>
					<Box display="flex" alignItems="center">
						{hasChildren && (
							<IconButton
								size="small"
								onClick={() => setOpen(!open)}
								sx={{ color: (theme) => getTextColor(theme), mr: 1, p: 0.5 }}
							>
								{open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
							</IconButton>
						)}
						<Typography variant="tableRowFont" sx={{ color: (theme) => getTextColor(theme) }}>
							<HighlightedText text={row.name} />
						</Typography>
					</Box>
				</TableCell>
				<TableCell
					align="right"
					sx={{
						'&.MuiTableCell-root': {
							color: (theme) => getTextColor(theme),
							fontSize: '14px',
							backgroundColor: (theme) => `${getRowBackground(theme)} !important`,
							borderRight:(theme) => `1px solid ${theme.palette.white.main} !important`,
							padding: '8px 12px !important',
							width: '200px'
						}
					}}
				>
					<Typography variant="tableRowFont">
						{formatNumberByUnits(row.totalSpent, true)}
					</Typography>
				</TableCell>
				<TableCell
					align="right"
					sx={{
						'&.MuiTableCell-root': {
							color: (theme) => getTextColor(theme),
							fontSize: '14px',
							backgroundColor: (theme) => `${getRowBackground(theme)} !important`,
							padding: '8px 12px !important',
							width: '200px'
						}
					}}
				>
					<Typography variant="tableRowFont">
						{row.percentage.toFixed(2)} %
					</Typography>
				</TableCell>
			</TableRow>
			{hasChildren && (
				<TableRow>
					<TableCell
						colSpan={3}
						sx={{
							'&.MuiTableCell-root': {
								paddingBottom: 0,
								paddingTop: 0,
								backgroundColor: 'transparent !important'
							}
						}}
					>
						<Collapse in={open} timeout="auto" unmountOnExit>
							<Box>
								<Table size="small" sx={{ bgcolor: 'transparent' }}>
									<TableBody>
										{row.children!.map((childRow, idx) => (
											<ExpandableTableRow key={idx} row={childRow} level={level + 1} />
										))}
									</TableBody>
								</Table>
							</Box>
						</Collapse>
					</TableCell>
				</TableRow>
			)}
		</>
	);
};

export default ExpandableTableRow;
